import React, { useState, useEffect, useRef } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  ArrowDropDownOutlined,
  Star as StarIcon,
  Close as CloseIcon,
  NotificationsOutlined,
  HelpOutlineOutlined,
  SettingsOutlined,
  VisibilityOutlined,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Drawer,
  useTheme,
  Badge,
  Avatar,
  MenuItem,
  Divider,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setMode } from "state";
import { logout } from "state/authSlice";
import { useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import ScoreDashboard from "./ScoreDashboard";
import { useGetBestScorersQuery, useUpdatepasswordMutation } from "state/api";

const Navbar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { username, MgCd, EmpNo } = useSelector((state) => state.global);
  const [notifications] = useState([]);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isStarSidebarOpen, setIsStarSidebarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const passwordResetRef = useRef(null);

  const [updatePassword] = useUpdatepasswordMutation();
  const { data: bestScorers } = useGetBestScorersQuery();

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        (!passwordResetRef.current || !passwordResetRef.current.contains(event.target))
      ) {
        setShowProfileMenu(false);
        setIsUpdatingPassword(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Password reset state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handlePasswordReset = async () => {
    setPasswordError("");
    if (!newPassword || !confirmPassword) {
      return setPasswordError("Please enter both new password fields.");
    }
    if (newPassword !== confirmPassword) {
      return setPasswordError("Passwords do not match.");
    }
    try {
      const res = await updatePassword({ EmpNo, newpassword: newPassword }).unwrap();
      alert(res.message || "Password updated successfully.");
      setNewPassword("");
      setConfirmPassword("");
      setIsUpdatingPassword(false);
    } catch (err) {
      alert("Failed to update password.");
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/LoginPage");
  };

  const toggleStarSidebar = () => {
    setIsStarSidebarOpen((prev) => !prev);
    if (showProfileMenu) setShowProfileMenu(false);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu((prev) => !prev);
    if (isStarSidebarOpen) setIsStarSidebarOpen(false);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    if (showProfileMenu) setShowProfileMenu(false);
    if (isStarSidebarOpen) setIsStarSidebarOpen(false);
  };

  // Mobile menu content
  const mobileMenuContent = (
    <Box sx={{ p: 2 }}>
      <Button
        fullWidth
        onClick={toggleStarSidebar}
        startIcon={<StarIcon />}
        sx={{ justifyContent: 'flex-start', mb: 1 }}
      >
        Top Performers
      </Button>
      <Divider sx={{ my: 1 }} />
      <Button
        fullWidth
        startIcon={<HelpOutlineOutlined />}
        sx={{ justifyContent: 'flex-start', mb: 1 }}
      >
        Help Center
      </Button>
      <Button
        fullWidth
        startIcon={<NotificationsOutlined />}
        sx={{ justifyContent: 'flex-start', mb: 1 }}
      >
        Notifications
      </Button>
      <Button
        fullWidth
        startIcon={<SettingsOutlined />}
        sx={{ justifyContent: 'flex-start', mb: 1 }}
      >
        Settings
      </Button>
      <Button
        fullWidth
        onClick={() => dispatch(setMode())}
        startIcon={theme.palette.mode === "dark" ? <DarkModeOutlined /> : <LightModeOutlined />}
        sx={{ justifyContent: 'flex-start', mb: 1 }}
      >
        {theme.palette.mode === "dark" ? "Dark Mode" : "Light Mode"}
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{ 
          background: theme.palette.background.default, 
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: 1300 
        }}
      >
        <Toolbar sx={{ padding: isMobile ? "8px 16px" : "8px 24px" }}>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              onClick={toggleMobileMenu}
              sx={{ mr: 1, color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Star Performer Button - shown on all screens */}
          <Button
            onClick={toggleStarSidebar}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textTransform: "none",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              "&:hover": { 
                backgroundColor: theme.palette.primary.dark,
                boxShadow: `0 2px 8px ${theme.palette.primary.light}`
              },
              borderRadius: "12px",
              padding: "8px 16px",
              transition: "all 0.3s ease",
              mr: isMobile ? 'auto' : 2,
            }}
          >
            <StarIcon sx={{ color: "yellow", fontSize: "20px" }} />
            <span>{isMobile ? "Top" : "Top Performers"}</span>
          </Button>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <IconButton 
                sx={{ 
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main 
                  }
                }}
              >
                <HelpOutlineOutlined />
              </IconButton>

              <IconButton 
                sx={{ 
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main 
                  }
                }}
              >
                <Badge badgeContent={notifications.length} color="error">
                  <NotificationsOutlined />
                </Badge>
              </IconButton>

              <IconButton 
                sx={{ 
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main 
                  }
                }}
              >
                <SettingsOutlined />
              </IconButton>

              <IconButton 
                onClick={() => dispatch(setMode())}
                sx={{ 
                  color: theme.palette.text.secondary,
                  "&:hover": { 
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main 
                  }
                }}
              >
                {theme.palette.mode === "dark" ? (
                  <DarkModeOutlined />
                ) : (
                  <LightModeOutlined />
                )}
              </IconButton>
            </Box>
          )}

          {/* Profile Section - shown on all screens */}
          <Box sx={{ position: "relative", ml: isMobile ? 1 : 0 }}>
            <Button
              variant="text"
              onClick={toggleProfileMenu}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
                color: theme.palette.text.primary,
                "&:hover": { 
                  backgroundColor: theme.palette.action.hover 
                },
                borderRadius: "12px",
                padding: "6px 12px",
                minWidth: 'auto',
              }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem'
                }}
              >
                {username.charAt(0).toUpperCase()}
              </Avatar>
              {!isMobile && (
                <>
                  <Box textAlign="left">
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {username.trim().split(" ")[0] || "User"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {MgCd || "Role"}
                    </Typography>
                  </Box>
                  <ArrowDropDownOutlined fontSize="small" />
                </>
              )}
            </Button>

            {/* Profile Menu Dropdown */}
            {showProfileMenu && (
              <Box
                ref={profileMenuRef}
                sx={{
                  position: "absolute",
                  top: "56px",
                  right: 0,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[3],
                  borderRadius: "12px",
                  padding: "8px 0",
                  zIndex: 9999,
                  width: isMobile ? '100vw' : 280,
                  maxWidth: 'calc(100vw - 32px)',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {EmpNo || "Employee ID"}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ p: 2 }} ref={passwordResetRef}>
                  {!isUpdatingPassword ? (
                    <>
                      <MenuItem 
                        onClick={() => setIsUpdatingPassword(true)}
                        sx={{
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Typography variant="body2">Reset Password</Typography>
                      </MenuItem>
                      <MenuItem 
                        onClick={handleLogout}
                        sx={{
                          borderRadius: "8px",
                          color: theme.palette.error.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Typography variant="body2">Logout</Typography>
                      </MenuItem>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Reset Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton edge="end" size="small">
                                <VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      {passwordError && (
                        <Typography color="error" variant="caption">
                          {passwordError}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} mt={1}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setIsUpdatingPassword(false)}
                          size="small"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handlePasswordReset}
                          size="small"
                        >
                          Update
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: theme.palette.background.default,
            borderRight: `1px solid ${theme.palette.divider}`,
          }
        }}
      >
        {mobileMenuContent}
      </Drawer>

      {/* Star Performers Drawer */}
      <Drawer
        anchor="left"
        open={isStarSidebarOpen}
        onClose={toggleStarSidebar}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 320 },
            backgroundColor: theme.palette.background.default,
            borderRight: `1px solid ${theme.palette.divider}`,
          }
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <FlexBetween sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" fontWeight={600}>
              ⭐ Top Performers
            </Typography>
            <IconButton onClick={toggleStarSidebar} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </FlexBetween>

          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <ScoreDashboard />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;